import React from "react";

import { Button } from "src/common/components/Button";
import { Confirm, NoButton, YesButton } from "src/common/components/Confirm";
import { ConfirmContext } from "src/features/confirm/ConfirmContext";
import { ConfirmAction, ConfirmOptions } from "src/features/confirm/types";

interface ConfirmProviderState {
	isOpen: boolean;
	content: React.ReactNode;
	negativeActions: ConfirmAction[];
	positiveActions: ConfirmAction[];
	resolve: (value: unknown) => void;
}

export const ConfirmProvider: React.FunctionComponent<
	React.PropsWithChildren
> = ({ children }) => {
	const [
		{ isOpen, resolve, content, negativeActions, positiveActions },
		setState
	] = React.useState<ConfirmProviderState>({
		isOpen: false,
		resolve: () => {},
		content: null,
		negativeActions: [],
		positiveActions: []
	});

	const create = React.useCallback(
		({
			content,
			negativeActions = [],
			positiveActions = []
		}: ConfirmOptions): Promise<unknown> =>
			new Promise(resolve => {
				setState(state => ({
					...state,
					isOpen: true,
					content,
					negativeActions,
					positiveActions,
					resolve
				}));
			}),
		[]
	);

	return (
		<ConfirmContext.Provider value={create}>
			{children}
			{isOpen && (
				<Confirm
					content={content}
					negativeActions={
						negativeActions.length === 0 ? (
							<NoButton
								onClick={() => {
									setState(state => ({
										...state,
										isOpen: false
									}));
									resolve(false);
								}}
							/>
						) : (
							negativeActions.map(({ text, value }, i) => (
								<Button
									key={i}
									type="button"
									variant="secondary"
									className="confirm__button"
									onClick={() => {
										setState(state => ({
											...state,
											isOpen: false
										}));
										resolve(value);
									}}
								>
									{text}
								</Button>
							))
						)
					}
					positiveActions={
						positiveActions.length === 0 ? (
							<YesButton
								onClick={() => {
									setState(state => ({
										...state,
										isOpen: false
									}));
									resolve(true);
								}}
							/>
						) : (
							positiveActions.map(({ text, value }, i) => (
								<Button
									key={i}
									type="button"
									variant="primary"
									className="confirm__button"
									onClick={() => {
										setState(state => ({
											...state,
											isOpen: false
										}));
										resolve(value);
									}}
								>
									{text}
								</Button>
							))
						)
					}
				/>
			)}
		</ConfirmContext.Provider>
	);
};
